import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { scopeEventsForPersona } from '../hooks/useEventList.js';
import { throwDecisionSkipped } from '../utils/decisionSkipped.js';
import { useTranslation } from '@aimsos/i18n';
import { WorkQueueContext } from './WorkQueueContext.js';
import {
  aggregateWorkQueueEvents,
  resolveTask,
  resolveChangeRequest,
  createChangeOnChangeRequest,
  acceptClaimSuggestion,
  declineClaimSuggestion,
  suggestClaimChange,
  resolveHitlTask,
} from '../../../services/index.js';
import {
  editCandidateClaim,
  markCandidateClaimReviewed,
  removeClaimsFromPromotion,
  submitPromotion,
} from '../../../services/promotionActions.js';
import {
  fetchPromotionDetailSnapshot,
  mergePromotionDetailSnapshot,
} from '../utils/refreshPromotionEventDetail.js';
import {
  closeEventThread as closeEventThreadRequest,
  reopenEventThread as reopenEventThreadRequest,
  markQuestionRead as markQuestionReadRequest,
  postThreadComment as postThreadCommentRequest,
  createAskQuestion as createAskQuestionRequest,
} from '../../../services/threadActions.js';
import { eventIsCustomer } from '../utils/customerClassification.js';
import { countDueBuckets } from '../utils/dueBuckets.js';
import { MOCK_ATTESTATIONS, MOCK_THREADS, SCOPE } from '../constants/mockEvents.js';
import { buildQuestionThreadSeed } from '../utils/buildQuestionThreadSeed.js';
import { getDevSeedHitlEvents } from '../constants/devSeedHitl.js';
import { getBreakGlassThreadFixture } from '../constants/breakGlassFixtures.js';
import { useCurrentPersona } from '../hooks/useCurrentPersona.js';
import { EVENT_TYPE } from '../constants/eventTypes.js';
import { SEVERITY } from '../constants/severities.js';
import { DUE_BUCKET } from '../constants/filterOptions.js';
import { initialsFromName } from '../utils/initials.js';

function getTrainMeAuditAction(t, outcome, { value, note, reason }) {
  if (outcome === 'promoted') {
    return t('trainMe.audit.promoted', { value, note: note || '—' });
  }
  if (outcome === 'rejected') {
    return t('trainMe.audit.rejected', { reason });
  }
  return outcome;
}

function getQuestionOutcomeCopy(t, outcome, { response, recipient, reason }) {
  switch (outcome) {
    // No response means the asker closed it from their own thread ("Mark
    // answered"), so there is nothing to append as a reply.
    case 'answered':
      return {
        threadBody: response ?? '',
        auditAction: response ? t('question.audit.answered') : t('question.audit.markedAnswered'),
      };
    case 'forwarded':
      return {
        threadBody: t('question.thread.forwarded', { recipient: recipient.name }),
        auditAction: t('question.audit.forwarded', { recipient: recipient.name }),
      };
    case 'declined':
      return {
        threadBody: t('question.thread.declined', { reason }),
        auditAction: t('question.audit.declined', { reason }),
      };
    default:
      return {
        threadBody: '',
        auditAction: outcome,
      };
  }
}

const EMPTY_FILTERS = {
  search: '',
  severity: null, // quick-chip severity (card view)
  inboxCriticalOnly: false, // All / Critical chips (inbox split)
  team: [],
  studio: [],
  type: [],
  due: null,
};

/**
 * Holds the shared Work Queue state. `events` is fetched from the governance
 * service, keyed by `scope`, via {@link aggregateWorkQueueEvents} — the interim
 * fan-out (CT-5091) that stands in until the unified events endpoint (CT-4867)
 * ships. `scope` is shared so Card view and Inbox view stay in sync across routes.
 *
 * `initialEvents` seeds the list and skips the network entirely — used by tests
 * (and any embed that wants to hydrate synchronously). When omitted, the provider
 * starts empty and fetches on mount / scope change.
 */
const WorkQueueProvider = ({ children, initialEvents }) => {
  const { t } = useTranslation('work-queues-app');
  const currentPersona = useCurrentPersona();
  const [events, setEvents] = useState(initialEvents ?? []);
  const [loading, setLoading] = useState(!initialEvents);
  const [error, setError] = useState(null);
  // Thread / audit trail are per-event, in-memory optimistic stores. Real
  // persistence is blocked on CT-4867 (no endpoint), so they start empty — the
  // action flows below append to them. MOCK_THREADS/MOCK_AUDIT_TRAIL are gone
  // from the live path (CT-5092): they were keyed by mock event ids and never
  // matched real GS uuids anyway.
  const [auditTrails, setAuditTrails] = useState({});
  const [threads, setThreads] = useState({});
  const [scope, setScope] = useState(SCOPE.MY_WORK);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [skippedIds, setSkippedIds] = useState(() => new Set());
  const [resolvedIds, setResolvedIds] = useState(() => new Set());
  /** Event ids with an in-flight governance decision POST — skeleton + disabled footer. */
  const [pendingDecisionIds, setPendingDecisionIds] = useState(() => new Set());
  /** Bumps per event to invalidate cached GS detail previews after a partial attestation failure. */
  const [previewEpochByEventId, setPreviewEpochByEventId] = useState({});
  const bumpEventPreview = useCallback((id) => {
    setPreviewEpochByEventId((current) => ({ ...current, [id]: (current[id] ?? 0) + 1 }));
  }, []);
  const [sort, setSort] = useState({ by: 'severity', direction: 'desc' });
  const [userNameById, setUserNameById] = useState({});
  const [aggregateWarnings, setAggregateWarnings] = useState([]);
  const [serverTotals, setServerTotals] = useState(null);
  const eventsRef = useRef(events);
  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);
  /** Event ids whose resolve already persisted to GS — a later failed retry must not reopen them. */
  const resolvedSuccessfullyRef = useRef(new Set());
  /** In-flight decision calls — ref for guards, state for UI skeleton/disabled button. */
  const pendingDecisionRef = useRef(new Set());

  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  /** Per-scope filter stats for filter bars — shared so InboxFilterBar avoids a second useEventList pass. */
  const typeCountsByScope = useMemo(() => {
    const build = (scope) => {
      const personaScoped = scopeEventsForPersona(events, scope, currentPersona);
      const scoped = personaScoped.filter(
        (event) => !skippedIds.has(event.id) && !resolvedIds.has(event.id)
      );
      let customerCount = 0;
      const typeCounts = scoped.reduce((acc, event) => {
        if (event.type) acc[event.type] = (acc[event.type] ?? 0) + 1;
        if (eventIsCustomer(event)) customerCount += 1;
        return acc;
      }, {});
      return { typeCounts, dueCounts: countDueBuckets(scoped), customerCount };
    };
    return {
      [SCOPE.MY_WORK]: build(SCOPE.MY_WORK),
      [SCOPE.MY_TEAM]: build(SCOPE.MY_TEAM),
    };
  }, [events, currentPersona, skippedIds, resolvedIds]);

  // Guard against a fan-out resolving after the provider unmounts. Set on mount
  // AND cleared on unmount so StrictMode's mount→unmount→remount leaves it `true`.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadEvents = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const {
        events: fetched,
        userNameById: names,
        warnings,
        serverTotals: totals,
      } = await aggregateWorkQueueEvents({ scope });
      // Ignore a stale response (scope changed / another load started / unmounted).
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setUserNameById(names ?? {});
      setAggregateWarnings(warnings ?? []);
      setServerTotals(totals ?? null);
      // DEV-ONLY: optional mock HITL rows (localStorage `wq_seed_hitl`) for prototype
      // fixtures without a live agent run. Real pending tasks come from hitl/list in
      // aggregateWorkQueueEvents. See constants/devSeedHitl.js.
      setEvents([...fetched, ...getDevSeedHitlEvents()]);
    } catch (err) {
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setError(err?.message ?? 'Failed to load the queue.');
      setAggregateWarnings([]);
      setServerTotals(null);
      // Keep the DEV-seeded HITL rows visible even if the live fetch failed (e.g. an
      // expired session) so the HITL footers can still be QA'd locally. No-op unless
      // the `wq_seed_hitl` flag is set in a dev build. See constants/devSeedHitl.js.
      setEvents(getDevSeedHitlEvents());
    } finally {
      if (mountedRef.current && fetchId === fetchIdRef.current) setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    // Seeded (tests / synchronous embed) — never hit the network.
    if (initialEvents) return;
    loadEvents();
  }, [initialEvents, loadEvents]);

  const setFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => setFilters(EMPTY_FILTERS), []);

  const skipEvent = useCallback((id) => {
    setSkippedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const updateEventOwner = useCallback((eventId, owner) => {
    setEvents((current) =>
      current.map((event) => (event.id === eventId ? { ...event, owner } : event))
    );
  }, []);

  const appendAuditEntry = useCallback((eventId, entry) => {
    setAuditTrails((current) => ({
      ...current,
      [eventId]: [...(current[eventId] ?? []), entry],
    }));
  }, []);

  const resolveThreadForEvent = useCallback((eventId, stored) => {
    const event = eventsRef.current.find((entry) => entry.id === eventId);
    const seed =
      MOCK_THREADS[eventId] ??
      getBreakGlassThreadFixture(eventId) ??
      (event?.type === EVENT_TYPE.QUESTION ? buildQuestionThreadSeed(event) : null);
    const status = stored?.status ?? seed?.status ?? 'open';

    // User/appended comments win; otherwise always mirror the card into the thread.
    if (stored?.comments?.length) {
      return { ...stored, status };
    }
    if (seed?.comments?.length) {
      return { ...seed, status };
    }
    return stored ?? { status: 'open', comments: [] };
  }, []);

  const appendThreadEntry = useCallback(
    (eventId, entry) => {
      setThreads((current) => {
        const thread = resolveThreadForEvent(eventId, current[eventId]);
        return {
          ...current,
          [eventId]: { ...thread, comments: [...thread.comments, entry] },
        };
      });
    },
    [resolveThreadForEvent]
  );

  const postThreadComment = useCallback(
    async (eventId, { body, mentions = [] } = {}) => {
      const text = body?.trim();
      if (!eventId || !text) return;
      const saved = await postThreadCommentRequest({ eventId, body: text, mentions });
      appendThreadEntry(eventId, {
        id: saved.commentId,
        author: currentPersona.name,
        role: t(`question.roles.${currentPersona.role}`),
        initials: currentPersona.initials,
        timeAgo: t('team.justNow'),
        body: saved.body,
        mentions: mentions.map((person) => person.name),
      });
    },
    [appendThreadEntry, currentPersona, t]
  );

  const createQuestion = useCallback(
    async ({ sourceEvent, recipient, question, context, dueBy }) => {
      const askedBy = {
        ...currentPersona,
        role: t(`question.roles.${currentPersona.role}`),
      };
      const mock = await createAskQuestionRequest({
        sourceEventId: sourceEvent.id,
        recipient,
        question,
        context,
        dueBy,
        askedBy,
      });
      const { questionEventId: id, threadEntry } = mock;
      const dueLabel = dueBy || t('status.awaiting_reply');

      setEvents((current) => [
        ...current,
        {
          id,
          scope: SCOPE.MY_WORK,
          type: EVENT_TYPE.QUESTION,
          category: sourceEvent.category,
          severity: SEVERITY.ACTION,
          title: mock.question,
          description: mock.context || mock.question,
          refLabel: t('eventType.question'),
          actionLabel: t('question.reply'),
          studio: sourceEvent.studio,
          studioExtraCount: 0,
          status: 'awaiting_reply',
          dueLabel,
          dueBucket: DUE_BUCKET.THIS_WEEK,
          overdue: false,
          isCustomer: false,
          missionCritical: false,
          blocks: { workflows: 0, agents: 0 },
          commentsCount: 1,
          situation: mock.context || mock.question,
          pipeline: [t('actions.ask'), t('question.reply')],
          owner: {
            id: recipient.id,
            name: recipient.name,
            initials: initialsFromName(recipient.name),
            isMine: recipient.id === currentPersona.id,
          },
          detail: {
            askedBy,
            recipient,
            question: mock.question,
            context: mock.context,
            dueBy,
            linkedEvent: { id: sourceEvent.id, title: sourceEvent.title },
          },
          destination: sourceEvent.destination,
        },
      ]);
      setThreads((current) => ({
        ...current,
        [id]: {
          status: 'open',
          comments: [
            {
              id: `question-${id}`,
              author: currentPersona.name,
              role: askedBy.role,
              initials: currentPersona.initials,
              timeAgo: t('team.justNow'),
              body: mock.question,
            },
          ],
        },
      }));
      appendThreadEntry(sourceEvent.id, {
        ...threadEntry,
        timeAgo: t('team.justNow'),
      });
      appendAuditEntry(sourceEvent.id, {
        id: `question-created-${id}`,
        actor: currentPersona.name,
        action: t('question.audit.asked', { recipient: recipient.name, question: mock.question }),
        timeAgo: t('team.justNow'),
      });
      return id;
    },
    [appendAuditEntry, appendThreadEntry, currentPersona, t]
  );

  const completeQuestion = useCallback(
    async (eventId, { outcome, response, recipient, reason }) => {
      const questionEvent = eventsRef.current.find((event) => event.id === eventId);
      if (!questionEvent) return;

      // CT-5096: `answered`/`declined` resolve the HITL interrupt (POST
      // /v1/tasks/hitl/resolve → modified / rejected); `forwarded` is a local
      // reassign, not a resolve. Only the resolving paths get the pending/skeleton
      // + success-gating contract (mirrors resolveChangeRequestEvent).
      const resolving = outcome === 'answered' || outcome === 'declined';
      if (resolving) {
        if (
          resolvedSuccessfullyRef.current.has(eventId) ||
          pendingDecisionRef.current.has(eventId)
        ) {
          throwDecisionSkipped();
        }
        pendingDecisionRef.current.add(eventId);
        setPendingDecisionIds((current) => new Set(current).add(eventId));
      }
      try {
        if (resolving) {
          // Rows from hitl/list carry `detail.contractId` → POST resolve. Dev-seed
          // mock rows (wq_seed_hitl) omit contractId → local resolve only.
          const { contractId } = questionEvent.detail ?? {};
          if (contractId) {
            const trimmedResponse = response?.trim();
            const trimmedReason = reason?.trim();
            // `modified` carries the answer; `rejected` carries the decline reason the
            // confirm modal requires. Without it the workflow resumes as rejected with
            // no record of WHY the question was declined.
            let modifiedArgs;
            if (outcome === 'answered' && trimmedResponse) {
              modifiedArgs = { response: trimmedResponse };
            } else if (outcome === 'declined' && trimmedReason) {
              modifiedArgs = { reason: trimmedReason };
            }
            const result = await resolveHitlTask({
              contractId,
              status: outcome === 'answered' ? 'modified' : 'rejected',
              modifiedArgs,
            });
            if (result == null) {
              const idsError = new Error('HITL task is missing the id required to resolve it.');
              idsError.code = 'IDS_MISSING';
              throw idsError;
            }
          }
        }

        const outcomeCopy = getQuestionOutcomeCopy(t, outcome, { response, recipient, reason });

        setEvents((current) =>
          current.map((event) => {
            if (event.id !== eventId) return event;
            return {
              ...event,
              status: outcome,
              owner:
                outcome === 'forwarded'
                  ? {
                      id: recipient.id,
                      name: recipient.name,
                      initials: initialsFromName(recipient.name),
                      isMine: recipient.id === currentPersona.id,
                    }
                  : event.owner,
            };
          })
        );
        // Same success-gate as every other resolver: mark the ref BEFORE the state
        // update so a second concurrent call is skipped by the guard above instead of
        // firing a duplicate POST /v1/tasks/hitl/resolve.
        if (resolving) resolvedSuccessfullyRef.current.add(eventId);
        setResolvedIds((current) => new Set(current).add(eventId));

        const threadTargetId = questionEvent?.detail?.linkedEvent?.id ?? eventId;
        setThreads((current) => {
          const thread =
            current[threadTargetId] ??
            MOCK_THREADS[threadTargetId] ??
            (questionEvent.type === EVENT_TYPE.QUESTION
              ? buildQuestionThreadSeed(questionEvent)
              : { status: 'open', comments: [] });
          const updated = thread.comments.map((comment) =>
            comment.questionEventId === eventId
              ? { ...comment, questionStatus: outcome }
              : comment
          );
          const comments = outcomeCopy.threadBody
            ? [
                ...updated,
                {
                  id: `question-result-${Date.now()}`,
                  author: currentPersona.name,
                  role: t(`question.roles.${currentPersona.role}`),
                  initials: currentPersona.initials,
                  timeAgo: t('team.justNow'),
                  body: outcomeCopy.threadBody,
                },
              ]
            : updated;
          const nextThread = { ...thread, comments };
          // Prototype: only declining closes the conversation panel; answering keeps
          // the thread open so the response appears below the question seed.
          if (outcome === 'declined' && threadTargetId === eventId) {
            nextThread.status = 'closed';
          }
          const next = { ...current, [threadTargetId]: nextThread };
          if (outcome === 'declined' && threadTargetId !== eventId) {
            const ownThread = resolveThreadForEvent(eventId, current[eventId]);
            next[eventId] = { ...ownThread, status: 'closed' };
          }
          return next;
        });
        appendAuditEntry(eventId, {
          id: `question-${outcome}-${Date.now()}`,
          actor: currentPersona.name,
          action: outcomeCopy.auditAction,
          timeAgo: t('team.justNow'),
        });
      } finally {
        if (resolving) {
          pendingDecisionRef.current.delete(eventId);
          setPendingDecisionIds((current) => {
            const next = new Set(current);
            next.delete(eventId);
            return next;
          });
        }
      }
    },
    [appendAuditEntry, currentPersona, resolveThreadForEvent, t]
  );

  /**
   * Resolve an interim `task` event (CT-5093) → POST /v1/task/resolve/{task_uuid}.
   * While the call is in flight the detail panel shows a skeleton and Resolve is
   * disabled; the event leaves the inbox only after GS confirms success. On
   * failure the task stays put and the caller toasts.
   */
  const completeTask = useCallback(
    async (eventId) => {
      const target = eventsRef.current.find((event) => event.id === eventId);
      if (!target) return;
      if (resolvedSuccessfullyRef.current.has(eventId)) return;
      if (pendingDecisionRef.current.has(eventId)) return;

      pendingDecisionRef.current.add(eventId);
      setPendingDecisionIds((current) => new Set(current).add(eventId));
      try {
        // DEV-ONLY seed tasks skip the network (fake uuid would 404) → clean success.
        if (!target.detail?.__seed) {
          const { taskUuid, entityType, entityUuid } = target.detail ?? {};
          const result = await resolveTask({ taskUuid, entityType, entityUuid });
          if (result == null) {
            const idsError = new Error('Task is missing the backend ids required to resolve it.');
            idsError.code = 'TASK_IDS_MISSING';
            throw idsError;
          }
        }
        resolvedSuccessfullyRef.current.add(eventId);
        setResolvedIds((current) => new Set(current).add(eventId));
        appendAuditEntry(eventId, {
          id: `task-resolved-${Date.now()}`,
          actor: currentPersona.name,
          action: t('task.auditResolved'),
          timeAgo: t('team.justNow'),
        });
      } finally {
        pendingDecisionRef.current.delete(eventId);
        setPendingDecisionIds((current) => {
          const next = new Set(current);
          next.delete(eventId);
          return next;
        });
      }
    },
    [appendAuditEntry, currentPersona, t]
  );

  /**
   * Resolve an interim `change_request` event (CT-5095 — the Gov Review footer) via
   * the governance service. `approve`/`reject` → POST /v1/my-work/change-request/resolve;
   * `correct` files a counter-proposal → POST …/create-on-change-request. Same
   * optimistic-on-success + pending/skeleton contract as {@link completeTask}: the
   * event leaves the inbox only after GS confirms; on failure it stays and the caller
   * toasts. (Backend note: `approve` currently 500s — CT-4867.)
   */
  const resolveChangeRequestEvent = useCallback(
    async (eventId, { action, correction, reason } = {}) => {
      const target = eventsRef.current.find((event) => event.id === eventId);
      if (
        !target ||
        resolvedSuccessfullyRef.current.has(eventId) ||
        pendingDecisionRef.current.has(eventId)
      ) {
        throwDecisionSkipped();
      }

      pendingDecisionRef.current.add(eventId);
      setPendingDecisionIds((current) => new Set(current).add(eventId));
      try {
        const { changeRequestUuid } = target.detail ?? {};
        const result =
          action === 'correct'
            ? await createChangeOnChangeRequest({ changeRequestUuid, suggestedText: correction })
            : await resolveChangeRequest({ changeRequestUuid, action, reason });
        if (result == null) {
          const idsError = new Error('Change request is missing the id required to resolve it.');
          idsError.code = 'IDS_MISSING';
          throw idsError;
        }
        appendAuditEntry(eventId, {
          id: `cr-${action}-${Date.now()}`,
          actor: currentPersona.name,
          action: t(`changeRequest.audit.${action}`),
          timeAgo: t('team.justNow'),
        });
        // Approve/Reject resolve the CR → the card leaves the inbox. `correct`
        // files a counter-proposal (create-on-change-request) but the PARENT CR
        // stays PENDING_APPROVAL, so the card must STAY actionable — matching the
        // beta My Work "Propose change" (verified 2026-09-03: it never removes the row).
        if (action !== 'correct') {
          resolvedSuccessfullyRef.current.add(eventId);
          setResolvedIds((current) => new Set(current).add(eventId));
        }
      } finally {
        pendingDecisionRef.current.delete(eventId);
        setPendingDecisionIds((current) => {
          const next = new Set(current);
          next.delete(eventId);
          return next;
        });
      }
    },
    [appendAuditEntry, currentPersona, t]
  );

  /**
   * Resolve an interim `claim_suggestion` event (CT-5095 — the Train Me footer) via
   * the governance service. `promoted` → accept, `rejected` → decline (both use
   * `detail.suggestionUuid` from the list fan-out — GAP-1 resolved in mapper),
   * `suggested` → a counter-suggestion (works off `claimUuid`; response uuid is
   * persisted locally so a follow-up promote uses the fresh id). Same success-gated
   * contract as {@link completeTask}.
   */
  const resolveClaimSuggestion = useCallback(
    async (eventId, { outcome, suggestedText, reason } = {}) => {
      const target = eventsRef.current.find((event) => event.id === eventId);
      if (
        !target ||
        resolvedSuccessfullyRef.current.has(eventId) ||
        pendingDecisionRef.current.has(eventId)
      ) {
        throwDecisionSkipped();
      }

      pendingDecisionRef.current.add(eventId);
      setPendingDecisionIds((current) => new Set(current).add(eventId));
      try {
        const { claimUuid, suggestionUuid } = target.detail ?? {};
        let result;
        if (outcome === 'suggested') {
          result = await suggestClaimChange({ claimUuid, suggestedText, rationale: reason });
        } else if (outcome === 'promoted') {
          result = await acceptClaimSuggestion({ suggestionUuid });
        } else {
          result = await declineClaimSuggestion({ suggestionUuid, reason });
        }
        if (result == null) {
          // No id to act on. For accept/decline that means GAP-1 (the pending
          // suggestion's uuid isn't exposed by list/preview); for suggest, no claimUuid.
          const idsError = new Error('This suggestion is missing the id required to act on it.');
          idsError.code = outcome === 'suggested' ? 'IDS_MISSING' : 'SUGGESTION_ID_MISSING';
          throw idsError;
        }
        appendAuditEntry(eventId, {
          id: `claim-${outcome}-${Date.now()}`,
          actor: currentPersona.name,
          action: t(`claimSuggestion.audit.${outcome}`),
          timeAgo: t('team.justNow'),
        });
        // Counter-suggestion: keep the card; refresh uuid + proposed text so the next
        // Promote (unchanged) hits accept on the new suggestion, not another suggest.
        if (outcome === 'suggested') {
          const nextUuid = result?.body?.suggestionUuid ?? result?.body?.suggestion_uuid;
          const nextText = (suggestedText ?? '').trim() || null;
          setEvents((current) =>
            current.map((event) => {
              if (event.id !== eventId) return event;
              return {
                ...event,
                situation: nextText ?? event.situation,
                description: nextText ?? event.description,
                detail: {
                  ...event.detail,
                  ...(nextUuid ? { suggestionUuid: nextUuid } : {}),
                  ...(nextText ? { suggestedText: nextText } : {}),
                },
              };
            })
          );
        }
        // Accept/decline resolve the pending suggestion → the claim leaves the inbox.
        // `suggested` files a NEW counter-suggestion (the claim still has pending
        // suggestions), so the card must STAY — matching the beta My Work, which
        // refetches and keeps the row (verified 2026-09-03).
        if (outcome !== 'suggested') {
          resolvedSuccessfullyRef.current.add(eventId);
          setResolvedIds((current) => new Set(current).add(eventId));
        }
      } finally {
        pendingDecisionRef.current.delete(eventId);
        setPendingDecisionIds((current) => {
          const next = new Set(current);
          next.delete(eventId);
          return next;
        });
      }
    },
    [appendAuditEntry, currentPersona, t]
  );

  /**
   * Resolve an interim HITL event (CT-5096 — Continuation / Handoff / Break Glass
   * footers) via the HITL Tasks API. The footer's terminal decision maps to
   * `decision.status` (approved / rejected / modified) → POST /v1/tasks/hitl/resolve
   * on the PLATFORM base (raw HTTP, not the GS envelope). Same success-gated +
   * pending/skeleton contract as {@link resolveChangeRequestEvent}.
   *
   * Rows without `detail.contractId` (dev-seed mocks) resolve locally. Rows from
   * `hitl/list` POST /v1/tasks/hitl/resolve and leave the inbox only on success.
   */
  const resolveHitlEvent = useCallback(
    async (eventId, { status, modifiedArgs, auditAction, eventStatus = 'resolved' } = {}) => {
      const target = eventsRef.current.find((event) => event.id === eventId);
      if (
        !target ||
        resolvedSuccessfullyRef.current.has(eventId) ||
        pendingDecisionRef.current.has(eventId)
      ) {
        throwDecisionSkipped();
      }

      pendingDecisionRef.current.add(eventId);
      setPendingDecisionIds((current) => new Set(current).add(eventId));
      try {
        const { contractId } = target.detail ?? {};
        if (contractId) {
          const result = await resolveHitlTask({ contractId, status, modifiedArgs });
          if (result == null) {
            const idsError = new Error('HITL task is missing the id required to resolve it.');
            idsError.code = 'IDS_MISSING';
            throw idsError;
          }
        }
        setEvents((current) =>
          current.map((event) => (event.id === eventId ? { ...event, status: eventStatus } : event))
        );
        resolvedSuccessfullyRef.current.add(eventId);
        setResolvedIds((current) => new Set(current).add(eventId));
        if (auditAction) {
          appendAuditEntry(eventId, {
            id: `hitl-${status}-${Date.now()}`,
            actor: currentPersona.name,
            action: auditAction,
            timeAgo: t('team.justNow'),
          });
        }
      } finally {
        pendingDecisionRef.current.delete(eventId);
        setPendingDecisionIds((current) => {
          const next = new Set(current);
          next.delete(eventId);
          return next;
        });
      }
    },
    [appendAuditEntry, currentPersona, t]
  );

  const completeTrainMe = useCallback(
    (eventId, { outcome, value, note, reason }) => {
      if (!eventsRef.current.some((event) => event.id === eventId)) return;

      setEvents((current) =>
        current.map((event) => (event.id === eventId ? { ...event, status: outcome } : event))
      );
      setResolvedIds((current) => new Set(current).add(eventId));
      appendAuditEntry(eventId, {
        id: `trainme-${outcome}-${Date.now()}`,
        actor: currentPersona.name,
        action: getTrainMeAuditAction(t, outcome, { value, note, reason }),
        timeAgo: t('team.justNow'),
      });
    },
    [appendAuditEntry, currentPersona, t]
  );

  const resolveEventWithClaimCorrections = useCallback(
    (eventId, corrections, audit) => {
      if (!eventsRef.current.some((event) => event.id === eventId)) return;

      setEvents((current) =>
        current.map((event) => {
          if (event.id !== eventId) return event;
          const claims = event.detail?.claims?.map((claim) =>
            Object.hasOwn(corrections, claim.id)
              ? {
                  ...claim,
                  originalText: claim.originalText ?? claim.text,
                  text: corrections[claim.id],
                }
              : claim
          );
          return {
            ...event,
            status: 'resolved',
            detail: claims ? { ...event.detail, claims } : event.detail,
          };
        })
      );
      setResolvedIds((current) => new Set(current).add(eventId));
      appendAuditEntry(eventId, audit);
    },
    [appendAuditEntry]
  );

  const completeGovPromotion = useCallback(
    async (
      eventId,
      { promoted, rejected, flagged = 0, corrected = 0, corrections = {}, decisions = {}, claims: providedClaims },
    ) => {
      const target = eventsRef.current.find((event) => event.id === eventId);
      if (
        !target ||
        resolvedSuccessfullyRef.current.has(eventId) ||
        pendingDecisionRef.current.has(eventId)
      ) {
        throwDecisionSkipped();
      }

      const { promotionUuid } = target.detail ?? {};

      pendingDecisionRef.current.add(eventId);
      setPendingDecisionIds((current) => new Set(current).add(eventId));
      try {
        // A live promotion's provider-side copy never carries `detail.claims` — that
        // list only exists on the preview-enriched event `useEventDetail` renders
        // (fetched from GET .../promotion/claims/{uuid}), never merged back into this
        // provider's own state. The caller (GovPromotionDecision) already holds that
        // enriched list and passes it in; `target.detail?.claims` only covers
        // dev/test fixtures that seed claims directly on the raw event.
        const claims = providedClaims ?? target.detail?.claims ?? [];

        // Prototype contract: per-claim Approve/Reject/Correct are draft-only until
        // Confirm Attestation — nothing hits GS until this block runs.
        // TEMPORARY: workspace `/v1/workspace/promotion/*` until My Work promotions (CT-4867).
        if (promotionUuid && claims.length) {
          // Corrections are independent per-claim writes — fan them out concurrently
          // instead of one round-trip at a time. `allSettled` (not `all`) so every
          // fired request is guaranteed to finish before we decide success/failure —
          // otherwise a rejection would race the catch block's GS re-sync below
          // against requests `all` abandoned mid-flight, showing a stale snapshot.
          const correctionSettlements = await Promise.allSettled(
            claims
              .filter(
                (claim) =>
                  claim.candidateClaimUuid && decisions[claim.id] === 'correct' && corrections[claim.id],
              )
              .map((claim) => editCandidateClaim(claim.candidateClaimUuid, { claimText: corrections[claim.id] })),
          );
          const failedCorrection = correctionSettlements.find((r) => r.status === 'rejected');
          if (failedCorrection) throw failedCorrection.reason;

          const rejectedClaimUuids = claims
            .filter((claim) => claim.candidateClaimUuid && decisions[claim.id] === 'reject' && claim.claimUuid)
            .map((claim) => claim.claimUuid);

          if (rejectedClaimUuids.length) {
            await removeClaimsFromPromotion(promotionUuid, rejectedClaimUuids);
          }

          // Same reasoning — mark-review calls are independent per-claim writes.
          const reviewSettlements = await Promise.allSettled(
            claims
              .filter(
                (claim) =>
                  claim.candidateClaimUuid &&
                  decisions[claim.id] !== 'reject' &&
                  claim.reviewStatus !== 'reviewed',
              )
              .map((claim) => markCandidateClaimReviewed(claim.candidateClaimUuid)),
          );
          const failedReview = reviewSettlements.find((r) => r.status === 'rejected');
          if (failedReview) throw failedReview.reason;

          const result = await submitPromotion(promotionUuid);
          if (result == null) {
            const idsError = new Error('Promotion is missing the id required to submit it.');
            idsError.code = 'IDS_MISSING';
            throw idsError;
          }
        }

        resolveEventWithClaimCorrections(eventId, corrections, {
          id: `govpromo-attested-${Date.now()}`,
          actor: currentPersona.name,
          action: t('govPromotion.audit.attested', { promoted, rejected, flagged, corrected }),
          timeAgo: t('team.justNow'),
        });
        resolvedSuccessfullyRef.current.add(eventId);
      } catch (err) {
        // Partial batch failure: re-sync claims from GS so the reviewer sees which
        // rows already landed (reviewed/removed) before retrying attestation.
        if (promotionUuid) {
          try {
            const snapshot = await fetchPromotionDetailSnapshot(promotionUuid);
            if (snapshot) {
              setEvents((current) =>
                current.map((event) =>
                  event.id === eventId ? mergePromotionDetailSnapshot(event, snapshot) : event,
                ),
              );
              bumpEventPreview(eventId);
            }
          } catch {
            // Best-effort — the attestation error still surfaces to the caller.
          }
        }
        throw err;
      } finally {
        pendingDecisionRef.current.delete(eventId);
        setPendingDecisionIds((current) => {
          const next = new Set(current);
          next.delete(eventId);
          return next;
        });
      }
    },
    [resolveEventWithClaimCorrections, currentPersona, t, bumpEventPreview]
  );

  const completeGovReview = useCallback(
    (eventId, { approved, rejected, flagged = 0, corrected = 0, corrections = {} }) => {
      resolveEventWithClaimCorrections(eventId, corrections, {
        id: `govreview-submitted-${Date.now()}`,
        actor: currentPersona.name,
        action: t('govReview.audit.submitted', { approved, rejected, flagged, corrected }),
        timeAgo: t('team.justNow'),
      });
    },
    [resolveEventWithClaimCorrections, currentPersona, t]
  );

  const markQuestionRead = useCallback(async (eventId) => {
    await markQuestionReadRequest(eventId);
    setEvents((current) =>
      current.map((event) =>
        event.id === eventId && event.status === 'awaiting_reply'
          ? { ...event, status: 'read', dueLabel: t('question.markAsRead') }
          : event
      )
    );
  }, [t]);

  const closeEventThread = useCallback(
    async (eventId) => {
      await closeEventThreadRequest(eventId);
      setThreads((current) => {
        const base = resolveThreadForEvent(eventId, current[eventId]);
        return { ...current, [eventId]: { ...base, status: 'closed' } };
      });
    },
    [resolveThreadForEvent]
  );

  const reopenEventThread = useCallback(
    async (eventId) => {
      await reopenEventThreadRequest(eventId);
      setThreads((current) => {
        const base = resolveThreadForEvent(eventId, current[eventId]);
        return { ...current, [eventId]: { ...base, status: 'open' } };
      });
    },
    [resolveThreadForEvent]
  );

  const getEventById = useCallback((id) => events.find((e) => e.id === id) ?? null, [events]);
  const getPreviewEpoch = useCallback((id) => previewEpochByEventId[id] ?? 0, [previewEpochByEventId]);
  const getAuditTrail = useCallback((id) => auditTrails[id] ?? [], [auditTrails]);
  const getAttestation = useCallback((id) => MOCK_ATTESTATIONS[id] ?? [], []);
  const getThread = useCallback(
    (id) => resolveThreadForEvent(id, threads[id]),
    [threads, resolveThreadForEvent]
  );

  const value = useMemo(
    () => ({
      events,
      loading,
      error,
      total: events.length,
      refetch: loadEvents,
      userNameById,
      aggregateWarnings,
      serverTotals,
      typeCountsByScope,
      currentPersona,
      scope,
      setScope,
      filters,
      setFilter,
      clearFilters,
      sort,
      setSort,
      skippedIds,
      resolvedIds,
      pendingDecisionIds,
      skipEvent,
      updateEventOwner,
      appendAuditEntry,
      postThreadComment,
      createQuestion,
      completeQuestion,
      markQuestionRead,
      closeEventThread,
      reopenEventThread,
      completeTask,
      resolveChangeRequestEvent,
      resolveClaimSuggestion,
      resolveHitlEvent,
      completeTrainMe,
      completeGovPromotion,
      completeGovReview,
      getEventById,
      getPreviewEpoch,
      getAuditTrail,
      getAttestation,
      getThread,
    }),
    [
      events,
      loading,
      error,
      loadEvents,
      userNameById,
      aggregateWarnings,
      serverTotals,
      typeCountsByScope,
      currentPersona,
      scope,
      filters,
      setFilter,
      clearFilters,
      sort,
      skippedIds,
      resolvedIds,
      pendingDecisionIds,
      skipEvent,
      updateEventOwner,
      appendAuditEntry,
      postThreadComment,
      createQuestion,
      completeQuestion,
      markQuestionRead,
      closeEventThread,
      reopenEventThread,
      completeTask,
      resolveChangeRequestEvent,
      resolveClaimSuggestion,
      resolveHitlEvent,
      completeTrainMe,
      completeGovPromotion,
      completeGovReview,
      getEventById,
      getPreviewEpoch,
      getAuditTrail,
      getAttestation,
      getThread,
    ]
  );

  return <WorkQueueContext.Provider value={value}>{children}</WorkQueueContext.Provider>;
};

export default WorkQueueProvider;
